import React, { useEffect, useRef, useState } from 'react';
import WModal from 'shared_components/components/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import {
  FIELD_ERROR_CLASS,
  FIELD_LABEL_CLASS,
  FIELD_LABEL_ROW_CLASS,
  FIELD_OPTIONAL_CLASS,
  MODAL_ACTIONS_CLASS,
  fieldControlClass,
} from './formStyles';
import {
  CONTRIBUTION_ALIAS_REQUIRED_ERROR,
  hasContributionAlias,
} from '../../../utils/recordingValidators';

/**
 * Contribution detail modal — the "Contribution Confidence" editor.
 *
 * Extracted from RecordingEditForm so the exact same modal can be reused by the
 * bulk-edit builder. It owns the Alias / Data Quality / Evidence / Notes form
 * and, on save, hands back a normalised payload
 * ({ contribution, confidenceCategory, confidencePattern, personaId }).
 *
 * The alias is required for *every* mode. It gates `add` for the obvious
 * reason, and it gates `edit` because the backend rewrites the contribution
 * edge with `source_id = persona_id` on an edit too — an edit sent without one
 * writes an edge with an empty source rather than failing (see
 * `hasContributionAlias`).
 */

/** Data quality categories (shared source of truth). */
export const CONFIDENCE_CATEGORIES: {
  id: string;
  label: string;
  color: string;
  textColor: string;
}[] = [
  {
    id: '1',
    label: 'Verified (No Effort)',
    color: '#6EE038',
    textColor: 'black',
  },
  {
    id: '2',
    label: 'Inferred (Low Effort)',
    color: '#F6FF00',
    textColor: 'black',
  },
  {
    id: '3',
    label: 'Credited (Mid Effort)',
    color: '#FF8A00',
    textColor: 'white',
  },
  {
    id: '4',
    label: 'Unverified (High Effort)',
    color: '#FF2600',
    textColor: 'white',
  },
  {
    id: '5',
    label: 'Unknown (Max Effort)',
    color: '#AAB2B9',
    textColor: 'black',
  },
];

/** Validate an optional evidence URL. Returns an error string or null. */
export function validateEvidenceUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null; // optional field

  const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed);
  let parsed: URL;
  try {
    parsed = new URL(hasScheme ? trimmed : `http://${trimmed}`);
  } catch {
    return 'Enter a valid URL (e.g. google.com)';
  }
  if (!parsed.hostname || !parsed.hostname.includes('.')) {
    return 'Enter a valid URL (e.g. google.com)';
  }
  return null;
}

const capitalize = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

export interface ContributionAliasOption {
  stageName: string;
  personaId: string;
}

/** The editable shape held by the form. */
export interface ContributionDetailValue {
  contributionName: string;
  confidenceCategory: string;
  evidenceUrl: string;
  notes: string;
  personaId: string;
}

/** Normalised payload handed back on save (matches the recording contribution
 *  shape expected by the edit form / API). */
export interface ContributionSavePayload {
  contribution: string;
  confidenceCategory: string;
  confidencePattern: string;
  personaId?: string;
}

const EMPTY_VALUE: ContributionDetailValue = {
  contributionName: '',
  confidenceCategory: '1',
  evidenceUrl: '',
  notes: '',
  personaId: '',
};

interface ContributionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Whether this opens a brand-new contribution or amends an existing one.
   *  Both require an alias; the mode only picks the wording. */
  mode: 'add' | 'edit';
  /** Seed values shown when the modal opens. */
  initialValue?: Partial<ContributionDetailValue>;
  aliases: ContributionAliasOption[];
  aliasesLoading?: boolean;
  onSave: (payload: ContributionSavePayload) => void;
}

export const ContributionDetailModal: React.FC<
  ContributionDetailModalProps
> = ({
  isOpen,
  onClose,
  mode,
  initialValue,
  aliases,
  aliasesLoading = false,
  onSave,
}) => {
  const [form, setForm] = useState<ContributionDetailValue>(EMPTY_VALUE);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [aliasError, setAliasError] = useState<string | null>(null);

  // Seed the form each time the modal opens. Intentionally only depends on
  // `isOpen` so re-renders while open don't clobber in-progress edits.
  const seedRef = useRef(initialValue);
  seedRef.current = initialValue;
  useEffect(() => {
    if (isOpen) {
      setForm({ ...EMPTY_VALUE, ...seedRef.current });
      setEvidenceError(null);
      setAliasError(null);
    }
  }, [isOpen]);

  const handleSave = () => {
    // Checked before the evidence URL so the blocking problem is the one the
    // user is told about first.
    if (!hasContributionAlias(form.personaId)) {
      setAliasError(CONTRIBUTION_ALIAS_REQUIRED_ERROR);
      return;
    }
    setAliasError(null);

    const evidenceUrl = form.evidenceUrl?.trim() ?? '';
    const error = validateEvidenceUrl(evidenceUrl);
    if (error) {
      setEvidenceError(error);
      return;
    }
    setEvidenceError(null);
    onSave({
      contribution: form.contributionName,
      confidenceCategory: form.confidenceCategory,
      confidencePattern: JSON.stringify({
        evidenceUrl,
        notes: form.notes?.trim() ?? '',
      }),
      personaId: form.personaId,
    });
  };

  return (
    <WModal
      isOpen={isOpen}
      onClose={onClose}
      title="Contribution"
      modalClasses="tw-max-w-md tw-w-full tw-mx-4"
    >
      <div className="tw-mt-4 tw-space-y-4">
        <div>
          <span className="tw-block tw-text-textSeal tw-text-xs tw-font-medium tw-mb-1">
            Contribution
          </span>
          <p className="tw-text-white tw-text-sm">
            {form.contributionName ? capitalize(form.contributionName) : '—'}
          </p>
        </div>
        <div>
          <label
            htmlFor="contribution-modal-alias"
            className={`${FIELD_LABEL_CLASS} tw-mb-2`}
          >
            Alias
          </label>
          <select
            id="contribution-modal-alias"
            value={form.personaId}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, personaId: e.target.value }));
              if (aliasError) setAliasError(null);
            }}
            disabled={aliasesLoading}
            className={fieldControlClass({
              kind: 'select',
              hasValue: !!form.personaId,
              error: !!aliasError,
            })}
          >
            <option value="">
              {aliasesLoading ? 'Loading aliases…' : 'Select alias'}
            </option>
            {aliases.map((alias) => (
              <option key={alias.personaId} value={alias.personaId}>
                {alias.stageName}
              </option>
            ))}
          </select>
          {aliasError && (
            <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>{aliasError}</p>
          )}
        </div>
        <div>
          <label
            htmlFor="contribution-modal-confidence"
            className={`${FIELD_LABEL_CLASS} tw-mb-2`}
          >
            Data Quality Category
          </label>
          <select
            id="contribution-modal-confidence"
            value={form.confidenceCategory}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                confidenceCategory: e.target.value,
              }))
            }
            className={fieldControlClass({
              kind: 'select',
              hasValue: !!form.confidenceCategory,
            })}
          >
            {CONFIDENCE_CATEGORIES.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <div className={FIELD_LABEL_ROW_CLASS}>
            <label
              htmlFor="contribution-modal-evidence"
              className={FIELD_LABEL_CLASS}
            >
              Evidence URL
            </label>
            <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
          </div>
          <input
            id="contribution-modal-evidence"
            type="url"
            value={form.evidenceUrl}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, evidenceUrl: e.target.value }));
              if (evidenceError) setEvidenceError(null);
            }}
            placeholder="https://..."
            className={fieldControlClass({ error: !!evidenceError })}
          />
          {evidenceError && (
            <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>{evidenceError}</p>
          )}
        </div>
        <div>
          <div className={FIELD_LABEL_ROW_CLASS}>
            <label
              htmlFor="contribution-modal-notes"
              className={FIELD_LABEL_CLASS}
            >
              Notes
            </label>
            <span className={FIELD_OPTIONAL_CLASS}>Optional</span>
          </div>
          <textarea
            id="contribution-modal-notes"
            value={form.notes}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, notes: e.target.value }))
            }
            placeholder="Optional notes"
            rows={3}
            className={fieldControlClass({ kind: 'textarea' })}
          />
        </div>
        <div className={MODAL_ACTIONS_CLASS}>
          <WButton variant="secondary" label="Cancel" onClick={onClose} />
          <WButton
            variant="primary"
            label="Save"
            disabled={!hasContributionAlias(form.personaId)}
            onClick={handleSave}
          />
        </div>
      </div>
    </WModal>
  );
};

export default ContributionDetailModal;
